import type { WeekStartsOn } from '@crv/contracts/time-tracking';
import { formatUtcInstantForDisplay } from '../timezones/timezone-utils.js';

export const WEEK_STARTS_ON: WeekStartsOn = 1;

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// This helper uses host-local calendar semantics and should stay out of
// operational station-timezone code paths.
export function getWeekStartDate(
  value: Date,
  weekStartsOn: WeekStartsOn = WEEK_STARTS_ON,
): Date {
  const date = new Date(value);
  const day = date.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  date.setDate(date.getDate() - diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function getLocalDateInTimeZone(now: Date, timeZone: string): string | null {
  const formatted = formatUtcInstantForDisplay(now.toISOString(), timeZone, {
    includeSeconds: false,
    emptyFallback: '',
    invalidFallback: '',
  });
  const [datePart] = formatted.split(' ');

  if (!datePart || !LOCAL_DATE_PATTERN.test(datePart)) {
    return null;
  }

  return datePart;
}

export function shiftLocalDate(localDate: string, dayDelta: number): string | null {
  const parsed = parseLocalDate(localDate);
  if (!parsed) {
    return null;
  }

  const utcDate = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
  utcDate.setUTCDate(utcDate.getUTCDate() + dayDelta);
  return formatLocalDate(utcDate);
}

export function getWeekStartLocalDate(
  localDate: string,
  weekStartsOn: WeekStartsOn = WEEK_STARTS_ON,
): string | null {
  const parsed = parseLocalDate(localDate);
  if (!parsed) {
    return null;
  }

  const utcDate = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
  const dayOfWeek = utcDate.getUTCDay();
  const diff = (dayOfWeek - weekStartsOn + 7) % 7;
  utcDate.setUTCDate(utcDate.getUTCDate() - diff);
  return formatLocalDate(utcDate);
}

function parseLocalDate(value: string): { year: number; month: number; day: number } | null {
  if (!LOCAL_DATE_PATTERN.test(value)) {
    return null;
  }

  const [yearPart, monthPart, dayPart] = value.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);

  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year
    || probe.getUTCMonth() + 1 !== month
    || probe.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
}

function formatLocalDate(value: Date): string {
  const year = String(value.getUTCFullYear()).padStart(4, '0');
  const month = String(value.getUTCMonth() + 1).padStart(2, '0');
  const day = String(value.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
